import { useState } from 'react';
import { useExportMobileAppPdf } from '@/features/mobile-app/mobile-app.mutations';
import { uuidv4 } from '@/utils/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, Download, Loader2, Sparkles } from 'lucide-react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { FaRegFilePdf } from 'react-icons/fa6';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const APP_STORE_URL = 'https://apps.apple.com/gr/app/easymanage-app/id6758245692';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=gr.easymanage.app';

const buildQrUrl = (data: string, size = 260) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(
    data
  )}`;

export const Route = createFileRoute('/_app/(mobile-app)/mobile-app/')({
  component: MobileAppScreen,
});

type StoreCardProps = {
  variant: 'ios' | 'android';
  title: string;
  subtitle: string;
  storeName: string;
  url: string;
  icon: React.ReactNode;
  buttonLabel: string;
};

function StoreCard({
  variant,
  title,
  subtitle,
  storeName,
  url,
  icon,
  buttonLabel,
}: StoreCardProps) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Ο σύνδεσμος αντιγράφηκε');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Αποτυχία αντιγραφής');
    }
  };

  return (
    <div
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl',
        variant === 'ios'
          ? 'border-neutral-800/60 bg-gradient-to-br from-neutral-900 via-neutral-900 to-black text-white'
          : 'border-emerald-700/40 bg-gradient-to-br from-emerald-600 via-emerald-700 to-green-800 text-white'
      )}
    >
      <div
        className={cn(
          'pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full opacity-30 blur-3xl transition-opacity group-hover:opacity-50',
          variant === 'ios' ? 'bg-white/10' : 'bg-white/20'
        )}
      />
      <div
        className={cn(
          'pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full opacity-20 blur-3xl',
          variant === 'ios' ? 'bg-blue-500' : 'bg-lime-300'
        )}
      />

      <div className='relative flex items-center gap-3 p-6'>
        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-xl',
            variant === 'ios' ? 'bg-white/10' : 'bg-white/15'
          )}
        >
          {icon}
        </div>
        <div className='flex flex-col'>
          <span className='text-xs font-medium uppercase tracking-wider opacity-70'>
            {subtitle}
          </span>
          <span className='text-lg font-semibold'>{storeName}</span>
        </div>
      </div>

      <div className='relative flex flex-col items-center gap-4 px-6 pb-6'>
        <div className='rounded-2xl bg-white p-4 shadow-lg ring-1 ring-white/40'>
          <img
            src={buildQrUrl(url)}
            alt={`QR code για ${title}`}
            className='h-48 w-48 md:h-52 md:w-52'
            loading='lazy'
          />
        </div>
        <p className='text-center text-sm font-medium opacity-90'>
          Σκανάρετε με την κάμερα του κινητού σας
        </p>
      </div>

      <div className='relative mt-auto flex flex-col gap-2 border-t border-white/10 bg-black/10 p-4 backdrop-blur-sm'>
        <Button
          asChild
          size='lg'
          className={cn(
            'h-11 w-full gap-2 font-semibold shadow-md',
            variant === 'ios'
              ? 'bg-white text-neutral-900 hover:bg-neutral-100'
              : 'bg-white text-emerald-800 hover:bg-emerald-50'
          )}
        >
          <a href={url} target='_blank' rel='noopener noreferrer'>
            <Download className='h-4 w-4' strokeWidth={2.5} />
            {buttonLabel}
          </a>
        </Button>
        <Button
          variant='ghost'
          size='sm'
          onClick={onCopy}
          className='h-9 w-full gap-2 text-xs font-medium text-white/90 hover:bg-white/10 hover:text-white'
        >
          <Copy className='h-3.5 w-3.5' />
          {copied ? 'Αντιγράφηκε!' : 'Αντιγραφή συνδέσμου'}
        </Button>
      </div>
    </div>
  );
}

function MobileAppScreen() {
  const { mutateAsync: exportPdf, isPending: isExporting } = useExportMobileAppPdf();

  const handleExportPdf = () => exportPdf({ uuid: uuidv4() });

  return (
    <div className='flex h-full flex-col overflow-y-auto px-4'>
      <div className='mx-auto flex w-full max-w-5xl flex-col gap-8'>
        {/* Header */}
        <div className='flex flex-col items-center gap-3 text-center'>
          <div className='inline-flex items-center gap-2 rounded-full border bg-primary/5 px-3 py-1 text-xs font-medium text-primary'>
            <Sparkles className='h-3.5 w-3.5' />
            Διαθέσιμο σε iOS &amp; Android
          </div>
          <h1 className='text-3xl font-bold tracking-tight md:text-4xl'>
            Κατεβάστε την εφαρμογή EasyManage
          </h1>
          <p className='max-w-2xl text-base text-muted-foreground'>
            Διαχειριστείτε το σχολείο σας από οπουδήποτε. Σκανάρετε τον κωδικό QR με το κινητό σας ή
            χρησιμοποιήστε το κουμπί για άμεση εγκατάσταση.
          </p>
          <Button
            variant='outline'
            size='sm'
            onClick={handleExportPdf}
            disabled={isExporting}
            className='mt-1 gap-2'
          >
            {isExporting ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            ) : (
              <FaRegFilePdf className='h-3.5 w-3.5' />
            )}
            Εξαγωγή ως PDF
          </Button>
        </div>

        {/* Store cards */}
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <StoreCard
            variant='ios'
            title='EasyManage για iOS'
            subtitle='Διαθέσιμο στο'
            storeName='App Store'
            url={APP_STORE_URL}
            icon={<FaApple className='h-7 w-7 text-white' />}
            buttonLabel='Λήψη από App Store'
          />
          <StoreCard
            variant='android'
            title='EasyManage για Android'
            subtitle='Λήψη από το'
            storeName='Google Play'
            url={PLAY_STORE_URL}
            icon={<FaGooglePlay className='h-6 w-6 text-white' />}
            buttonLabel='Λήψη από Google Play'
          />
        </div>
      </div>
    </div>
  );
}
